/**
 * WidgetLayoutEditor — Stream D (Paritaet R4)
 *
 * Gemeinsame Widget-Auswahl (Toggles + dnd-kit-Reihenfolge) fuer
 *  - das Inline-Panel auf /dashboard (DashboardCustomizer) und
 *  - die eigene Seite /dashboard/customize (v1-Pendant /dashboard_boxes).
 *
 * Free-User (meta.customizable === false) sehen die Liste vollstaendig, aber
 * mit Upsell-Panel und DEAKTIVIERTEM Speichern — genau wie v1
 * (app/views/dashboard_boxes/show.haml: Premium-Hinweis statt Speicherpfad).
 *
 * R4-Review F6: "deaktiviert" heisst ALLE Interaktionen — Drag, Eye-Toggle,
 * Zuruecksetzen und Speichern. Vorher waren nur Drag/Save gesperrt; ein
 * Free-User konnte per Eye-Toggle/Reset ein Layout in den localStorage
 * schreiben, das vom serverseitigen `effective`-Set abweicht (das Dashboard
 * zeigte weiter das Default-Set — die Bedienung wirkte kaputt).
 */
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core'
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { GripVertical, Eye, EyeOff, RotateCcw, Save } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Link } from '@tanstack/react-router'
import type { WidgetConfig, WidgetId, DashboardLayout } from './useDashboardLayout'
import type { PlanMeta } from './useDashboardWidgets'
import type { DashboardLayoutError } from './useDashboardWidgets'

interface SortableWidgetItemProps {
  widget: WidgetConfig
  onToggle: (id: WidgetId) => void
  disabled?: boolean
}

function SortableWidgetItem({ widget, onToggle, disabled }: SortableWidgetItemProps) {
  const { t } = useTranslation('dashboard')
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: widget.id,
    disabled,
  })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  }

  return (
    <div ref={setNodeRef} style={style} className="dashboard-customizer-item">
      <button
        type="button"
        className="drag-handle"
        {...attributes}
        {...listeners}
        aria-label={t('customizer.dragHandle')}
        disabled={disabled}
      >
        <GripVertical size={16} />
      </button>
      <span className="widget-label">{widget.label}</span>
      <button
        type="button"
        className={`toggle-btn ${widget.visible ? 'visible' : 'hidden'}`}
        onClick={() => onToggle(widget.id)}
        disabled={disabled}
        aria-label={
          widget.visible
            ? `${t('customizer.hideWidget')}: ${widget.label}`
            : `${t('customizer.showWidget')}: ${widget.label}`
        }
      >
        {widget.visible ? <Eye size={16} /> : <EyeOff size={16} />}
      </button>
    </div>
  )
}

export type WidgetLayoutEditorProps = {
  layout: DashboardLayout
  meta: PlanMeta
  onToggle: (id: WidgetId) => void
  onReorder: (activeId: WidgetId, overId: WidgetId) => void
  onReset: () => void
  /** Wenn gesetzt, wird ein expliziter Speichern-Button gerendert. */
  onSave?: () => void
  isSaving?: boolean
  isDirty?: boolean
  saveError?: DashboardLayoutError | null
  saveSucceeded?: boolean
}

export function WidgetLayoutEditor({
  layout,
  meta,
  onToggle,
  onReorder,
  onReset,
  onSave,
  isSaving,
  isDirty,
  saveError,
  saveSucceeded,
}: WidgetLayoutEditorProps) {
  const { t } = useTranslation('dashboard')
  const canSave = meta.customizable

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  )

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event
    if (over && active.id !== over.id) {
      onReorder(active.id as WidgetId, over.id as WidgetId)
    }
  }

  return (
    <div className="widget-layout-editor">
      {!canSave && (
        <div className="customizer-premium-hint" role="note" data-testid="customizer-premium-hint">
          <p>{t('customizer.premiumHint')}</p>
          <Link to="/plans" className="btn btn-sm btn-primary">
            {t('customizer.premiumCta')}
          </Link>
        </div>
      )}

      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext
          items={layout.widgets.map((w) => w.id)}
          strategy={verticalListSortingStrategy}
        >
          <div className="widget-list">
            {layout.widgets.map((widget) => (
              <SortableWidgetItem
                key={widget.id}
                widget={widget}
                onToggle={onToggle}
                disabled={!canSave}
              />
            ))}
          </div>
        </SortableContext>
      </DndContext>

      <div className="customizer-actions">
        <button
          type="button"
          className="reset-btn"
          onClick={onReset}
          disabled={!canSave || isSaving}
          data-testid="customizer-reset"
        >
          <RotateCcw size={14} />
          {t('customizer.reset')}
        </button>
        {onSave && (
          <button
            type="button"
            className="btn btn-sm btn-primary"
            onClick={onSave}
            disabled={!canSave || isSaving}
            data-testid="customizer-save"
          >
            <Save size={14} />
            {t('customizer.save')}
          </button>
        )}
      </div>

      {isSaving && <div className="saving-indicator">{t('customizer.saving')}</div>}
      {!isSaving && saveSucceeded && !isDirty && (
        <div className="saving-indicator" role="status" data-testid="customizer-saved">
          {t('customizer.saved')}
        </div>
      )}
      {saveError && (
        <div className="form-error" role="alert" data-testid="customizer-save-error">
          {saveError.code === 'premium_required'
            ? t('customizer.errors.premiumRequired')
            : saveError.code === 'invalid_widgets'
              ? t('customizer.errors.invalidWidgets')
              : t('customizer.errors.saveFailed')}
        </div>
      )}
    </div>
  )
}
